import { formatDefaultLocale, FormatLocaleDefinition } from 'd3-format';
import { objToMoment, momentToStr, guessDateFormat } from './date';

const defaultPrefixes = ['y', 'z', 'a', 'f', 'p', 'n', 'µ', 'm', '', 'k', 'M', 'G', 'T', 'P', 'E', 'Z', 'Y'];

const getLocaleFormat = (locale: string): FormatLocaleDefinition => {
  const de: FormatLocaleDefinition = {
    decimal: ',',
    thousands: '.',
    grouping: [3],
    currency: ['', '\u00a0€'], // no break space unicode
  };

  const uk: FormatLocaleDefinition = {
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: ['£', ''],
  };

  const us: FormatLocaleDefinition = {
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: ['$', ''],
  };

  if (locale === 'en-GB') return uk;
  if (locale === 'en-US') return us;
  return de;
};

const getLocalePrefix = (locale: string): string[] => {
  const de = ['y', 'z', 'a', 'f', 'p', 'nano', 'mikro', 'milli', '', ' Tsd.', ' Mio.', ' Mrd.', ' Bio.', ' Brd.', ' Tri.', ' Trd.', ' Qdi.'];
  const en = ['y', 'z', 'a', 'f', 'p', 'n', 'µ', 'm', '', 'k', 'million', 'billion', 'trillion', 'P', 'E', 'Z', 'Y'];

  if (locale.includes('en')) return en;
  return de;
};

type LocaleKeys = 'de-DE' | 'en-GB' | 'en-US' | string;

export const formatNumber = (obj: number, numberFormat: any, locale: LocaleKeys = 'de-DE') => {
  const formatObj = formatDefaultLocale(getLocaleFormat(locale));

  // float rounding hack
  // https://github.com/d3/d3-format/issues/27
  const roundMatch = numberFormat.match(/\.(\d)[~]?f/);
  if (roundMatch && roundMatch[1] > 0) {
    obj += Number.EPSILON * obj;
  }

  const strObj: string = formatObj.format(numberFormat)(obj);
  return swapLocalePrefix(strObj, locale);
};

export const formatField = (obj: any, fieldFormat?: any, addPreSuffix: boolean = true, inputLocale: LocaleKeys = 'de-DE'): any => {
  if (obj === undefined || obj === null) return '';
  if (!fieldFormat) return obj; // || typeof obj === 'string'

  let { formatType } = fieldFormat;
  const { date, number, locale, prefix, suffix } = fieldFormat;

  // TODO SCREAM AT SOMEONE!!
  if (formatType === undefined) {
    // TODO dirty hack if object that is inputed is already a moment object
    if (typeof obj !== 'object') {
      const momentGuess = guessDateFormat(obj);
      if (typeof obj === 'number' || !isNaN(+obj)) formatType = 'number';
      if (typeof obj === 'string' && momentGuess) formatType = 'date';
    }
  }

  if (typeof obj === 'object' && obj.constructor && obj.constructor.name === 'Date') {
    formatType = 'date';
  }

  if (formatType === 'date' && date) {
    const { inputFormat, dateFormat } = date;
    if (typeof obj === 'object') {
      obj = momentToStr(obj, dateFormat, locale);
    } else {
      obj = objToMoment(obj, inputFormat, inputLocale);
      obj = momentToStr(obj, dateFormat, locale);
    }
  }

  if (formatType === 'number' && !Number.isNaN(obj)) {
    let { numberFormat, showSign } = number;
    const formatObj = formatDefaultLocale(getLocaleFormat(locale));

    // float rounding hack
    // https://github.com/d3/d3-format/issues/27
    const roundMatch = numberFormat.match(/\.(\d)[~]?f/);
    if (roundMatch && roundMatch[1] > 0) {
      obj += Number.EPSILON * obj;
    }

    if (obj === 0 && showSign) {
      numberFormat = numberFormat.replace('+', '-');
    }

    obj = formatObj.format(numberFormat)(obj);
    obj = swapLocalePrefix(obj, locale);
  }

  if (addPreSuffix) {
    if (prefix && prefix.length > 0) {
      obj = prefix + obj;
    }

    if (suffix && suffix.length > 0) {
      obj = obj + suffix;
    }
  }

  return obj;
};

/**
 * Sadly i found no other solution than replacing the prefixes
 */
const swapLocalePrefix = (string: string, locale: string) => {
  const match = string.match(/[A-Za-z]/);
  if (match) {
    const idx = defaultPrefixes.indexOf(match[0]);
    string = string.replace(match[0], getLocalePrefix(locale)[idx]);
  }

  return string;
};
